import { DatePipe } from '@angular/common';
import { AuthenticationService } from '@core/auth/authentication.service';
import { ButtonComponent } from '@shared/components/ui/button/button.component';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { DialogService } from '@shared/services/dialog.service';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Badge } from '@shared/components/ui/badge/badge';
import { DeleteTrigger } from '@shared/components/ui/delete-trigger/delete-trigger';
import {
  DataTable,
  FilterDate,
  FilterOption,
  FilterSelect,
  FiltersContainer,
  Pagination,
  TableCellDirective,
  TableColumn,
} from '@shared/components/ui/list-components';
import { ErrorService } from '@shared/services/error.service';
import { GenericHttpResponse } from '@shared/services/interfaces/generic-http-response.interface';
import { SnackbarService } from '@shared/services/snackbar.service';

import { PageHeaderComponent } from '@shared/components/common/page-header/page-header.component';
import { Cliente } from '../../../crm/clientes/clientes.interface';
import { CrearStockVitrificado } from '../crear-stock-vitrificado/crear-stock-vitrificado';
import { EmbrionesService } from '../embriones.service';
import { OpsColor, OpsPunto, StockVitrificado } from '../embriones.interface';
import { TransferirPajuelas } from '../../../agendamientos/transferencias/transferir-pajuelas/transferir-pajuelas';
import { FilaPajuela, filasDePajuelas } from './stock-pajuelas.util';

/** 2026-10-05: filtros sobre las pajuelas ya cargadas (la tabla se pagina en el front). */
type CampoFiltro = 'tecnica' | 'fechaOpu' | 'fechaD7' | 'donante' | 'toro' | 'serieRack' | 'estado';
const CAMPOS_SELECT = ['tecnica', 'donante', 'toro', 'serieRack', 'estado'] as const;
const FILTROS_VACIOS: Record<CampoFiltro, string> = { tecnica: '', fechaOpu: '', fechaD7: '', donante: '', toro: '', serieRack: '', estado: '' };

@Component({
  selector: 'app-stock-embriones-congelados',
  imports: [
    Badge,
    ButtonComponent,
    DataTable,
    DatePipe,
    DeleteTrigger,
    FilterDate,
    FilterSelect,
    FiltersContainer,
    MatIcon,
    MatTooltip,
    PageHeaderComponent,
    Pagination,
    TableCellDirective,
  ],
  templateUrl: './stock-embriones-congelados.html',
})
export class StockEmbrionesCongelados implements OnInit {
  loading = false;
  stocksVitrificados: StockVitrificado[] = [];
  /** Una fila por pajuela, con las columnas del informe de PIVE. */
  private filasTodas: FilaPajuela[] = [];
  /** Las que pasan los filtros. */
  filas: FilaPajuela[] = [];
  /** Fila de totales al pie, sobre todas las filas filtradas: solo con un cliente filtrado. */
  totales: Record<string, unknown> | null = null;
  filtros: Record<CampoFiltro, string> = { ...FILTROS_VACIOS };
  /** Opciones de cada filtro: los valores que hay en el stock cargado. */
  opciones: Record<(typeof CAMPOS_SELECT)[number], FilterOption[]> = { tecnica: [], donante: [], toro: [], serieRack: [], estado: [] };
  // Se pagina en el front: las filas son pajuelas armadas acá y la selección abarca todas.
  currentPage = 1;
  perPage = 10;
  /** Pajuelas marcadas para transferir. */
  seleccionadas = new Set<number>();
  deleting = false;
  deletingId: number | null = null;
  clientes: Cliente[] = [];
  clienteId = '';
  // El OPS marca cada pajuela: filtrar por punto o color es como el laboratorio
  // busca un embrión concreto dentro de un rack.
  puntos: OpsPunto[] = [];
  colores: OpsColor[] = [];
  opsPuntoId = '';
  opsColorId = '';

  // Las mismas columnas y en el mismo orden que el informe de PIVE del cliente.
  columns: TableColumn[] = [
    { key: 'sel', header: '' },
    { key: 'cliente', header: 'Cliente' },
    // 2026-10-01: sin Termo ni Cañas.
    // { key: 'termo', header: 'Termo' },
    // { key: 'canas', header: 'Cañas' },
    { key: 'tecnica', header: 'Técnica' },
    { key: 'fecha_opu', header: 'Fecha OPU' },
    { key: 'fecha_d7', header: 'Fecha D7' },
    { key: 'donante', header: 'Donante' },
    // 2026-10-01: la raza va en la misma columna que la donante y que el toro.
    // { key: 'raza_donante', header: 'Raza' },
    { key: 'toro', header: 'Toro' },
    // { key: 'raza_toro', header: 'Raza' },
    { key: 'embriones', header: 'N° Emb' },
    { key: 'serie', header: 'Serie / ID rack' },
    { key: 'id_ops', header: 'ID/OPS' },
    { key: 'fecha_te', header: 'Fecha TE' },
    { key: 'te', header: 'TE' },
    { key: 'perdida', header: 'Pérdida' },
    { key: 'saldo', header: 'Saldo' },
    { key: 'estado', header: 'Estado' },
    { key: 'destino', header: 'Destino' },
    { key: 'acciones', header: '' },
  ];

  readonly auth = inject(AuthenticationService);
  /** Reservar pajuelas en una transferencia: crear la TE pide Agendamientos y cargarla, Laboratorio. */
  readonly puedeTransferir = this.auth.hasPermission('stock.congelados-te', 'create');

  get lastPage(): number {
    return Math.max(1, Math.ceil(this.filas.length / this.perPage));
  }

  get filasPagina(): FilaPajuela[] {
    const inicio = (this.currentPage - 1) * this.perPage;
    return this.filas.slice(inicio, inicio + this.perPage);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
  }

  get clientesOptions(): FilterOption[] {
    return this.clientes.map(cliente => ({ value: cliente.id, label: cliente.persona.nombre_generico }));
  }

  get puntosOptions(): FilterOption[] {
    return this.puntos.map(punto => ({ value: punto.id, label: punto.codigo }));
  }

  get coloresOptions(): FilterOption[] {
    return this.colores.map(color => ({ value: color.id, label: color.nombre }));
  }

  private embrionesService = inject(EmbrionesService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogService = inject(DialogService);
  private router = inject(Router);

  ngOnInit(): void {
    // Un usuario atado a un cliente ya recibe solo lo suyo: no hay qué elegir.
    if (!this.auth.clienteDelUsuario()) {
      this.loadClientes();
    }
    // 2026-10-05: sin filtros de OPS.
    // this.loadOps();
    this.getStocksVitrificados();
  }

  loadOps(): void {
    forkJoin([
      this.embrionesService.getOpsPuntos(),
      this.embrionesService.getOpsColores(),
    ]).subscribe({
      next: ([puntosResponse, coloresResponse]) => {
        this.puntos = puntosResponse.data || [];
        this.colores = coloresResponse.data || [];
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      },
    });
  }

  loadClientes(): void {
    this.embrionesService.comboClientes().subscribe({
      next: (response) => {
        if (response.status) {
          this.clientes = response.data;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      },
    });
  }

  getStocksVitrificados(): void {
    this.loading = true;
    this.embrionesService.getStockVitrificados({
      cli_cliente_id: this.clienteId ? Number(this.clienteId) : undefined,
      // param_ops_punto_id: this.opsPuntoId ? Number(this.opsPuntoId) : undefined,
      // param_ops_color_id: this.opsColorId ? Number(this.opsColorId) : undefined,
    }).subscribe({
      next: (response: GenericHttpResponse<StockVitrificado[]>) => {
        if (response.status && response.data) {
          this.stocksVitrificados = response.data;
          this.filasTodas = filasDePajuelas(response.data);
          this.armarOpciones();
          this.filtrar();
          // Lo marcado se conserva mientras la pajuela siga disponible.
          const disponibles = new Set(this.filasTodas.filter(f => f.pajuela.estado === 'disponible').map(f => f.id));
          this.seleccionadas = new Set([...this.seleccionadas].filter(id => disponibles.has(id)));
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loading = false;
    });
  }

  onClienteChange(value: string | number): void {
    this.clienteId = String(value);
    this.currentPage = 1;
    this.getStocksVitrificados();
  }

  onOpsPuntoChange(value: string | number): void {
    this.opsPuntoId = String(value);
    this.currentPage = 1;
    this.getStocksVitrificados();
  }

  onOpsColorChange(value: string | number): void {
    this.opsColorId = String(value);
    this.currentPage = 1;
    this.getStocksVitrificados();
  }

  onFiltro(campo: CampoFiltro, value: string | number): void {
    this.filtros = { ...this.filtros, [campo]: String(value ?? '') };
    this.currentPage = 1;
    this.filtrar();
  }

  private filtrar(): void {
    const f = this.filtros;
    this.filas = this.filasTodas.filter(fila =>
      (!f.tecnica || fila.tecnica === f.tecnica)
      && (!f.fechaOpu || (fila.fechaOpu ?? '').slice(0, 10) === f.fechaOpu)
      && (!f.fechaD7 || (fila.fechaD7 ?? '').slice(0, 10) === f.fechaD7)
      && (!f.donante || fila.donante === f.donante)
      && (!f.toro || fila.toro === f.toro)
      && (!f.serieRack || fila.serieRack === f.serieRack)
      && (!f.estado || fila.estado === f.estado));
    this.totales = this.clienteId ? sumarPajuelas(this.filas) : null;
    this.currentPage = Math.min(this.currentPage, this.lastPage);
  }

  private armarOpciones(): void {
    for (const campo of CAMPOS_SELECT) {
      const valores = [...new Set(this.filasTodas.map(fila => fila[campo]).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
      this.opciones[campo] = valores.map(valor => ({ value: valor, label: valor }));
    }
  }

  get hasActiveFilters(): boolean {
    // return !!this.clienteId || !!this.opsPuntoId || !!this.opsColorId;
    return !!this.clienteId || Object.values(this.filtros).some(Boolean);
  }

  clearFilters(): void {
    this.clienteId = '';
    // this.opsPuntoId = '';
    // this.opsColorId = '';
    this.filtros = { ...FILTROS_VACIOS };
    this.currentPage = 1;
    this.getStocksVitrificados();
  }

  openCreateVitrificadoModal(): void {
    this.dialogService.openForm(CrearStockVitrificado, {
      size: 'lg',
      data: { clienteId: this.clienteId ? Number(this.clienteId) : null },
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.getStocksVitrificados();
      }
    });
  }

  verDetalleVitrificado(stock: StockVitrificado): void {
    this.router.navigate(['/stock/embriones', stock.id]);
  }

  /** Borra el registro manual completo, con todas sus pajuelas. */
  deleteStockVitrificado(stock: StockVitrificado): void {
    this.deleting = true;
    this.deletingId = stock.id;

    this.embrionesService.deleteStockVitrificado(stock.id).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Stock vitrificado eliminado correctamente');
          this.getStocksVitrificados();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.deleting = false;
      this.deletingId = null;
    });
  }

  detailTooltip(stock: StockVitrificado): string {
    return stock.origen_laboratorio ? 'Ver detalle de origen laboratorio' : 'Ver detalle y editar';
  }

  // ---------------------------------------------------------------- transferir

  estaSeleccionada(fila: FilaPajuela): boolean {
    return this.seleccionadas.has(fila.id);
  }

  toggleSeleccion(fila: FilaPajuela): void {
    if (!this.puedeTransferir || fila.pajuela.estado !== 'disponible') return;

    if (this.seleccionadas.has(fila.id)) {
      this.seleccionadas.delete(fila.id);
    } else {
      this.seleccionadas.add(fila.id);
    }
  }

  /** Las marcadas, aunque un filtro las esconda. */
  get filasSeleccionadas(): FilaPajuela[] {
    return this.filasTodas.filter(fila => this.seleccionadas.has(fila.id));
  }

  get totalEmbrionesSeleccionados(): number {
    return this.filasSeleccionadas.reduce((total, fila) => total + fila.embriones, 0);
  }

  /** Una transferencia es de un solo cliente: no se mezclan pajuelas de clientes distintos. */
  get clientesSeleccionados(): number {
    return new Set(this.filasSeleccionadas.map(fila => fila.stock.cli_cliente_id)).size;
  }

  get puedeElegirTodas(): boolean {
    return !!this.clienteId && this.filas.some(fila => fila.pajuela.estado === 'disponible');
  }

  /** Marca todas las disponibles del cliente filtrado; si ya están todas, las desmarca. */
  toggleSeleccionarDisponibles(): void {
    const disponibles = this.filas.filter(fila => fila.pajuela.estado === 'disponible');

    if (disponibles.every(fila => this.seleccionadas.has(fila.id))) {
      this.seleccionadas.clear();
      return;
    }

    this.seleccionadas = new Set(disponibles.map(fila => fila.id));
  }

  transferir(): void {
    const seleccion = this.filasSeleccionadas;
    // 2026-09-30: pueden ser de clientes distintos (capitalización, venta de embriones).
    // if (seleccion.length === 0 || this.clientesSeleccionados !== 1) return;
    if (seleccion.length === 0) return;

    this.dialogService.openForm(TransferirPajuelas, {
      // 2026-09-29: "Nueva transferencia" lleva todos los campos del agendamiento.
      size: 'lg',
      config: { maxHeight: '90vh' },
      data: {
        clienteId: seleccion[0].stock.cli_cliente_id,
        pajuelaIds: seleccion.map(fila => fila.id),
        pajuelas: seleccion.map(fila => ({
          id: fila.id,
          etiqueta: `${fila.donante} × ${fila.toro}${fila.idOps ? ' · ' + fila.idOps : ''}`,
          embriones: fila.embriones,
        })),
      },
    });
  }
}

function sumarPajuelas(filas: FilaPajuela[]): Record<string, unknown> {
  const suma = (campo: 'embriones' | 'te' | 'perdida' | 'saldo') => filas.reduce((total, fila) => total + Number(fila[campo] ?? 0), 0);

  return { cliente: 'Total', embriones: suma('embriones'), te: suma('te'), perdida: suma('perdida'), saldo: suma('saldo') };
}
